import React from "react";
import {
    Check,
    ChevronRight,
    Loader2,
    PackageSearch,
    RefreshCw,
    X,
} from "lucide-react";

const getProductId = (product) => product?.id

const getCampaignCount = (product) => {
    const count = Number(product?.dependent_sequence_count);

    return Number.isFinite(count) && count > 0 ? count : 0;
};

const getUsageLabel = (count) => {
    if (count === 0) return "Not used in campaigns";
    if (count === 1) return "Used by 1 campaign";

    return `Used by ${count} campaigns`;
};

const formatDate = (product) => {
    const value = product?.created_at;

    if (!value) return "Date unavailable";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "Date unavailable";

    return `Created ${new Intl.DateTimeFormat("en", {
        month: "short",
        day: "numeric",
        year: "numeric",
    }).format(date)}`;
};

const getCountValue = (data, keys) => {
    for (const key of keys) {
        const value = data?.[key] ?? data?.status_counts?.[key];

        if (Number.isFinite(Number(value))) return Number(value);
    }

    return null;
};

const CampaignCountTooltip = ({ product, data, status, error }) => {
    const fallbackTotal = getCampaignCount(product);
    const rows = [
        [
            "Total campaign",
            getCountValue(data, ["total", "total_count", "total_sequences"]) ??
                fallbackTotal,
        ],
        ["Active", getCountValue(data, ["active", "active_count"])],
        ["Paused", getCountValue(data, ["paused", "paused_count"])],
        ["Completed", getCountValue(data, ["completed", "completed_count"])],
    ].filter(([, value]) => value !== null);

    return (
        <div className="pointer-events-none absolute left-0 top-full z-20 mt-2 hidden w-44 max-w-[calc(100vw-2rem)] rounded-xl border border-slate-200 bg-white p-2.5 text-left shadow-lg group-hover:block group-focus-within:block sm:p-3">
            {status === "loading" ? (
                <p className="flex items-center gap-2 text-xs text-slate-600">
                    <Loader2 size={13} className="animate-spin" />
                    Loading breakdown...
                </p>
            ) : status === "failed" ? (
                <p className="text-xs text-red-600">
                    {error || "Breakdown unavailable."}
                </p>
            ) : (
                <div className="space-y-1.5">
                    {rows.map(([label, value]) => (
                        <div
                            key={label}
                            className="flex items-center justify-between gap-3 text-xs"
                        >
                            <span className="text-slate-500">{label}</span>
                            <span className="font-semibold text-slate-900">
                                {value}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const ProductSelectableList = ({
    products,
    status,
    error,
    selectedProductId,
    selectedItemRef,
    onRetry,
    onSelect,
    onCampaignBadgeClick,
    onCampaignBadgeIntent,
    sequenceCountById,
    sequenceCountStatusById,
    sequenceCountErrorById,
    showDate = true,
    showDescription = true,
    showChevron = true,
    showSelectedCheck = false,
    showSelectedClear = false,
    emphasizeSelected = false,
    variant = "cards",
    emptyDescription = "Try another search or create a new product.",
    centerEmptyState = false,
    onClearSelected,
}) => {
    const safeProducts = Array.isArray(products) ? products : [];
    const isRowVariant = variant === "rows";

    if (status === "loading" || status === "idle") {
        return (
            <div className="flex items-center gap-2 rounded-xl border border-indigo-100 bg-indigo-50 p-3 text-sm font-semibold text-indigo-700 sm:p-4">
                <Loader2 size={16} className="animate-spin" />
                Loading products...
            </div>
        );
    }

    if (status === "failed") {
        return (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 sm:p-4">
                <p className="font-semibold">Unable to load products.</p>
                {error && <p className="mt-1">{error}</p>}
                <button
                    type="button"
                    onClick={onRetry}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 font-semibold hover:bg-red-50"
                >
                    <RefreshCw size={15} aria-hidden="true" />
                    Retry
                </button>
            </div>
        );
    }

    if (status === "succeeded" && safeProducts.length === 0) {
        if (centerEmptyState) {
            return (
                <div className="flex h-full min-h-full flex-col items-center justify-center px-4 py-8 text-center">
                    <PackageSearch
                        size={24}
                        className="text-slate-400"
                        aria-hidden="true"
                    />
                    <p className="mt-2 text-sm font-semibold text-slate-800">
                        No products found.
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                        Create a product to start a campaign on it.
                    </p>
                </div>
            );
        }

        return (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center sm:p-5">
                <PackageSearch
                    size={24}
                    className="mx-auto text-slate-400"
                    aria-hidden="true"
                />
                <p className="mt-2 text-sm font-semibold text-slate-800">
                    No products found.
                </p>
                <p className="mt-1 text-xs text-slate-500">
                    {emptyDescription}
                </p>
            </div>
        );
    }

    if (status !== "succeeded") return null;

    return (
        <div className={isRowVariant ? "" : "space-y-2"}>
            {safeProducts.map((product, index) => {
                const productId = getProductId(product);
                const campaignCount = getCampaignCount(product);
                const isSelected = productId === selectedProductId;
                const shouldShowSelectedClear =
                    isSelected &&
                    showSelectedClear &&
                    Boolean(onClearSelected);
                const canOpenCampaignBadge =
                    campaignCount > 0 && Boolean(onCampaignBadgeClick);
                const productItemClassName = isRowVariant
                    ? `border-b border-slate-200 py-3 transition-colors duration-200 ${
                          isSelected
                              ? "border-l-4 border-l-[#f97316] bg-[#fff7ed] pl-2 shadow-[inset_0_0_0_1px_rgba(249,115,22,0.22)]"
                              : "border-l-4 border-l-transparent odd:bg-slate-50 even:bg-white hover:bg-amber-50/50"
                      }`
                    : `rounded-xl border p-2.5 transition-all duration-200 sm:p-3 ${
                          isSelected && emphasizeSelected
                              ? "border-orange-400 bg-[#fff7ed] shadow-sm ring-2 ring-orange-100"
                              : isSelected
                                ? "border-orange-200 bg-[#fff7ed] shadow-sm ring-1 ring-orange-100"
                                : "border-slate-200 odd:bg-slate-50 even:bg-white hover:border-slate-300 hover:bg-amber-50/50"
                      }`;

                return (
                    <article
                        key={productId || `${product?.name || "product"}-${index}`}
                        ref={isSelected ? selectedItemRef : null}
                        className={`relative ${productItemClassName}`}
                        onClick={() => onSelect?.(product)}
                    >
                        <button
                            type="button"
                            disabled={!productId}
                            className={`w-full text-left disabled:cursor-not-allowed disabled:opacity-60 ${
                                isRowVariant ? "px-3" : ""
                            } ${shouldShowSelectedClear ? "pr-11" : ""}`}
                        >
                            <div className="flex items-start gap-2.5 sm:gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <h3
                                            className={`min-w-0 flex-1 truncate text-sm font-semibold ${
                                                isSelected
                                                    ? "text-[#7c2d12]"
                                                    : "text-slate-900"
                                            }`}
                                            title={product?.name || ""}
                                        >
                                            {product?.name || "Untitled product"}
                                        </h3>
                                        {isSelected &&
                                            showSelectedCheck &&
                                            !shouldShowSelectedClear && (
                                                <Check
                                                    size={15}
                                                    className="shrink-0 text-[#ea580c]"
                                                    aria-hidden="true"
                                                />
                                            )}
                                    </div>
                                    {showDescription && (
                                        <p
                                            className="mt-1 line-clamp-1 text-xs leading-4 text-slate-500 sm:line-clamp-2 sm:leading-5"
                                            title={product?.description || ""}
                                        >
                                            {product?.description ||
                                                "No description available."}
                                        </p>
                                    )}
                                </div>
                                {showChevron && (
                                    <ChevronRight
                                        size={18}
                                        className={`mt-0.5 shrink-0 transition ${
                                            isSelected
                                                ? "text-[#ea580c]"
                                                : "text-slate-400"
                                        }`}
                                        aria-hidden="true"
                                    />
                                )}
                            </div>
                        </button>

                        {shouldShowSelectedClear && (
                            <button
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onClearSelected?.();
                                }}
                                aria-label="Clear selected product"
                                title="Clear selected product"
                                className="absolute right-3 top-3 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-red-100 bg-red-50 text-red-600 transition hover:bg-red-100"
                            >
                                <X size={14} aria-hidden="true" />
                            </button>
                        )}

                        <div
                            className={`mt-2.5 flex min-w-0 items-end justify-between gap-2 sm:mt-3 ${
                                isRowVariant ? "px-3" : ""
                            }`}
                        >
                            {canOpenCampaignBadge ? (
                                <div
                                    className="group relative min-w-0 max-w-full flex-1"
                                    onMouseEnter={() =>
                                        onCampaignBadgeIntent?.(product)
                                    }
                                >
                                    <button
                                        type="button"
                                        onFocus={() =>
                                            onCampaignBadgeIntent?.(product)
                                        }
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onCampaignBadgeClick?.(product);
                                        }}
                                        className="inline-flex w-fit max-w-full truncate rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-100 transition hover:bg-indigo-100"
                                        title={getUsageLabel(campaignCount)}
                                    >
                                        {getUsageLabel(campaignCount)}
                                    </button>
                                    <CampaignCountTooltip
                                        product={product}
                                        data={sequenceCountById?.[productId]}
                                        status={
                                            sequenceCountStatusById?.[
                                                productId
                                            ] || "idle"
                                        }
                                        error={
                                            sequenceCountErrorById?.[productId]
                                        }
                                    />
                                </div>
                            ) : (
                                <span
                                    className={`inline-flex w-fit min-w-0 max-w-full truncate rounded-full px-2.5 py-1 text-xs font-medium ${
                                        campaignCount > 0
                                            ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100"
                                            : "bg-slate-50 text-slate-500 ring-1 ring-slate-200"
                                    }`}
                                    title={getUsageLabel(campaignCount)}
                                >
                                    {getUsageLabel(campaignCount)}
                                </span>
                            )}
                            {showDate && (
                                <p className="shrink-0 whitespace-nowrap text-right font-medium text-xs text-slate-400">
                                    {formatDate(product)}
                                </p>
                            )}
                        </div>
                    </article>
                );
            })}
        </div>
    );
};

export default ProductSelectableList;
